import { FormConfig } from 'components/form/FormConfig';
import { Form } from 'components/form/Form';
import { PartnershipMode } from 'core/agency/Agency';
import { RevenueSharingFormData } from 'core/revenueSharing/RevenueSharing';
import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { Form as BootstrapForm } from 'react-bootstrap';
import i18n from 'i18n';
import { getByteLength } from 'utils/StringUtil';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { DATE_FORMAT, getToday, PURPOSE_MAX_LENGTH } from './useRevenueSharingFormModel';
import styles from './revenueSharingForm.module.scss';

const partnershipModeOptions = createSelectOptionsFromEnum(PartnershipMode, 'common.partnershipMode.');

export const useRevenueSharingFormFields = (
  values: RevenueSharingFormData,
  getOriginalPercentageText: (partnershipModeId: PartnershipMode) => string
) => {

  const { purpose, isInstant, partnershipModeId } = values;
  const purposeLength = getByteLength(purpose);
  const originalPercentageText = getOriginalPercentageText(partnershipModeId);

  const basicSection = useMemo(
    () =>
      new FormConfig.SectionBuilder(
        new FormConfig.FieldsBuilder()
          .addFormikInput({
            label: i18n.t<string>('revenueSharingForm.labels.purpose'),
            name: 'purpose',
            className: styles.purposeInput,
            postText: `${purposeLength}/${PURPOSE_MAX_LENGTH}`,
            postTextClassName: styles.countLimit
          })
          .addFormikDatePicker({
            label: i18n.t<string>('revenueSharingForm.labels.effectiveDate'),
            name: 'effectiveDate',
            showTimePicker: false,
            format: DATE_FORMAT,
            inputFormat: DATE_FORMAT,
            minDate: getToday(),
            disabled: isInstant
          })
          .addFormikCheckbox({
            label: '',
            name: 'isInstant',
            checkboxLabel: i18n.t<string>('revenueSharingForm.labels.isInstant')
          })
          .build()
      )
        .withTitle(i18n.t<string>('revenueSharingForm.titles.basicSettingSection'))
        .build(),
    [purposeLength, isInstant]
  );

  const revenueSharingSection = useMemo(
    () =>
      new FormConfig.SectionBuilder(
        new FormConfig.FieldsBuilder()
          .addFormikSelect({
            label: i18n.t<string>('revenueSharingForm.labels.partnershipMode'),
            name: 'partnershipModeId',
            simpleValue: true,
            options: partnershipModeOptions
          })
          .addCustom({
            label: i18n.t<string>('revenueSharingForm.labels.originalPercentage'),
            name: 'originalPercentage',
            render: () => <BootstrapForm.Label className='col-form-label label'>{originalPercentageText}</BootstrapForm.Label>
          })
          .addFormikInputGroup({
            label: i18n.t<string>('revenueSharingForm.labels.percentage'),
            name: 'percentage',
            type: 'number',
            step: 0.01,
            postfix: '%',
            className: styles.percentageInput
          })
          .build()
      )
        .withTitle(i18n.t<string>('revenueSharingForm.titles.revenueSharingSection'))
        .build(),
    [originalPercentageText]
  );

  return useMemo(
    () =>
      new FormConfig.Builder()
        .addSection(basicSection)
        .addSection(revenueSharingSection)
        .build(),
    [basicSection, revenueSharingSection]
  );
};

export const RevenueSharingFormContent: React.FC<{
  getOriginalPercentageText: (partnershipModeId: PartnershipMode) => string;
}> = ({
  getOriginalPercentageText
}) => {

  const formikProps = useFormikContext<RevenueSharingFormData>();
  const formConfig = useRevenueSharingFormFields(formikProps.values, getOriginalPercentageText);

  const renderFormBtns = useCallback(() => (
    <>
      <Form.SubmitButton>{i18n.t<string>('common.buttons.submit')}</Form.SubmitButton>
      <Form.CancelButton />
    </>
  ), []);

  return (
    <Form
      formikProps={formikProps}
      formConfig={formConfig}
      renderFormBtns={renderFormBtns}
    />
  );
};
